// Grandes masses financières (M24, lot L6-A, D-272, D-273) : affichées seulement avec le droit financier ;
// chaque ligne donne l'état effectif du partage, sa provenance et l'état de la valeur. Une collectivité qui
// ne partage pas est listée « non partagé », sans montant. Montants (fictif) des collectivités fictives.
import { LIBELLES_MASSE_FINANCIERE, LIBELLES_PROVENANCE_PARTAGE, type MasseFinanciere } from '@cc/contrats';
import { Bouton, EtatVide, PuceEtat, Tableau, type ColonneTableau } from '@cc/ui';
import { Download, Lock } from 'lucide-react';
import { EtatRequete } from '../../commun/EtatRequete';
import { exporter, useFinances, type Filtres } from './api';
import { EtatSources } from './Communs';
import { libelleEtat, TON_ETAT_VALEUR, valeur } from './logique';
import styles from '../achats/achats.module.css';

interface LigneFinances {
  collectivite_id: string;
  nom: string;
  fictive: boolean;
  masse: MasseFinanciere;
  partage: boolean;
  provenance_partage: string;
  etat: string;
  montant: number | null;
}

export function SectionFinances({ droit, filtres }: { droit: boolean; filtres: Filtres }) {
  const finances = useFinances(filtres, droit);
  if (!droit) {
    return (
      <section className={styles.bloc} aria-labelledby="titre-finances">
        <h2 id="titre-finances">Grandes masses financières</h2>
        <EtatVide titre="Réservé au droit financier" icone={Lock}>
          <p>Votre institution ne dispose pas du droit financier : aucune colonne financière n&apos;est restituée, ni à l&apos;écran ni à l&apos;export.</p>
        </EtatVide>
      </section>
    );
  }
  const lignes = (finances.data?.elements ?? []) as unknown as LigneFinances[];
  const colonnes: ColonneTableau<LigneFinances>[] = [
    { id: 'nom', entete: 'Collectivité', enTeteLigne: true, masquable: false, cellule: (l) => `${l.nom}${l.fictive ? ' (fictive)' : ''}` },
    { id: 'masse', entete: 'Masse', cellule: (l) => LIBELLES_MASSE_FINANCIERE[l.masse] },
    { id: 'partage', entete: 'Partage', cellule: (l) => (l.partage ? 'partagé' : 'non partagé') },
    { id: 'provenance', entete: 'Provenance du partage', cellule: (l) => LIBELLES_PROVENANCE_PARTAGE[l.provenance_partage] ?? l.provenance_partage },
    { id: 'montant', entete: 'Montant', numerique: true, cellule: (l) => valeur(l.montant, 'montant', l.fictive) },
    { id: 'etat', entete: 'État', cellule: (l) => <PuceEtat ton={TON_ETAT_VALEUR[l.etat as keyof typeof TON_ETAT_VALEUR] ?? 'neutre'} libelle={libelleEtat(l.etat)} /> },
  ];
  const p = { ...filtres };
  return (
    <EtatRequete chargement={finances.isPending} erreur={finances.error} onReessayer={() => void finances.refetch()} lot="L6-A" fonction="Les grandes masses financières">
      <section className={styles.bloc} aria-labelledby="titre-finances">
        <h2 id="titre-finances">Grandes masses financières {finances.data?.periode ? `de ${finances.data.periode.mois_debut} à ${finances.data.periode.mois_fin}` : ''}</h2>
        <EtatSources couverture={finances.data?.couverture} />
        <div className={styles.actes}>
          <Bouton icone={Download} onClick={() => void exporter('finances', 'csv', p)}>Exporter en CSV</Bouton>
          <Bouton icone={Download} onClick={() => void exporter('finances', 'xlsx', p)}>Exporter en XLSX</Bouton>
        </div>
        {lignes.length === 0 ? (
          <EtatVide titre="Aucune collectivité">Aucune collectivité ne correspond à ces filtres.</EtatVide>
        ) : (
          <Tableau legende="Grandes masses financières" colonnes={colonnes} lignes={lignes} cleLigne={(l) => `${l.collectivite_id}:${l.masse}`} cleColonnes="supervision-finances" />
        )}
      </section>
    </EtatRequete>
  );
}
